import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoListDetailResponse, TodoListResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UpdateTodoListInput {
  name?: string;
  description?: string;
}

interface UseTodoListReturn {
  list: TodoListDetailResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  update: (input: UpdateTodoListInput) => Promise<TodoListResponse>;
  remove: () => Promise<void>;
}

export function useTodoList(listId: string): UseTodoListReturn {
  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(listId);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: detailKey,
    queryFn: () => api.getTodoListDetail(listId),
    enabled: !!listId,
  });

  const updateMut = useMutation({
    mutationFn: (input: UpdateTodoListInput) => api.updateTodoList(listId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
  });

  const removeMut = useMutation({
    mutationFn: () => api.deleteTodoList(listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.my() });
    },
  });

  return {
    list: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load todo list' : null,
    refresh: () => {
      refetch();
    },
    update: (input) => updateMut.mutateAsync(input),
    remove: () => removeMut.mutateAsync(),
  };
}
